import React, { useContext } from 'react';
import Modal from './Modal';
import {Form, Box, Text, FormField, TextInput, TextArea, ResponsiveContext} from 'grommet';
import InfoText from './InfoText';
import styled from 'styled-components';
import StatusMessage from './StatusMessage';
import { formatDateUTC } from '../utils/dates';
import { formatName } from '../utils/names';
import NextAnchor from './NextAnchor';
import { nonRedirectFetcher } from '../utils/errors';
import useSWR from 'swr';
import Button from './Button';
import { snakeCase } from '../utils/string';

// Inject height attribute to text area
const TallTextArea = styled(TextArea)`
    height: 300px;
`;

const PreviewEmailModal = ({
    onClose,
    data,
    onSubmit,
    onChange,
    formValues,
    title,
    submitBtnText,
    to,
    cc,
    secondaryBtnText,
    secondaryBtnOnClick,
    isScheduled=false
}) => {
    const size = useContext(ResponsiveContext);

    // senderDetailsData is only used to show the sender's name on the "From" line;
    // the email always sends from no-reply@app.streamlinedpayments.com. Use
    // nonRedirectFetcher so a 403 (users without settings/account access) degrades
    // to the no-reply fallback below instead of triggering the global 403 redirect
    // that bounces the user to the dashboard mid-send.
    const { data: senderDetailsData } = useSWR("/api/settings/account", nonRedirectFetcher, {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
    });

    if (!data) return (<></>);
    return (
        <Modal
            title={title}
            onClose={onClose}
            sectionProps={{ width: { min: 'large' } }}
            hideDeepLink>
            {isScheduled && (
                <StatusMessage type="info" message={<Text>This issue date for this invoice is in the future. It will be scheduled to be sent on <Text weight="bold">{formatDateUTC(data?.issued_at)}.</Text></Text>}/>
            )}
            <Form
                value={formValues}
                onChange={onChange}
                onSubmit={onSubmit}>
                <Box gap="xsmall" pad={{top: "small", bottom: 'small'}}>
                    <Box direction="row" gap="small" align="baseline">
                        <Box width="55px" flex={false}>
                            <Text size="small" color="text-weak">From</Text>
                        </Box>
                        <Box flex>
                            <Text truncate>
                                {senderDetailsData?.primary_contact ? formatName(senderDetailsData.primary_contact, true) : ''}
                                {senderDetailsData?.primary_contact && <Text color="text-weak"> &lt;no-reply@app.streamlinedpayments.com&gt;</Text>}
                                {!senderDetailsData?.primary_contact && <Text>no-reply@app.streamlinedpayments.com</Text>}
                            </Text>
                        </Box>
                    </Box>
                    {to && (
                        <Box direction="row" gap="small" align="baseline">
                            <Box width="55px" flex={false}>
                                <Text size="small" color="text-weak">To</Text>
                            </Box>
                            <Box flex>
                                <Text truncate>
                                    {to?.name || to?.email}
                                    {to?.name && <Text color="text-weak"> &lt;{to.email}&gt;</Text>}
                                </Text>
                            </Box>
                        </Box>
                    )}
                    {cc && cc.length > 0 && (
                        <Box direction="row" gap="small" align="baseline">
                            <Box width="55px" flex={false}>
                                <Text size="small" color="text-weak">Cc</Text>
                            </Box>
                            <Box flex>
                                <Text truncate color="text-weak">{cc.map(contact => contact?.email).join(", ")}</Text>
                            </Box>
                        </Box>
                    )}
                </Box>
                <Box direction="row" gap="small" align="center">
                    <Box width="55px" flex={false}>
                        <Text size="small" color="text-weak">Subject</Text>
                    </Box>
                    <Box fill>
                        <FormField
                            {...(isScheduled ? {disabled: true} : {})}
                            component={TextInput}
                            name="email_subject"
                            plain/>
                    </Box>
                </Box>
                <Box margin={{top: "small"}}>
                    <FormField
                        {...(isScheduled ? {disabled: true} : {})}
                        component={TallTextArea}
                        name="email_message"
                        placeholder="Message for customer"
                        resize={true}
                        info={(<Text size="small" color="text-weak">You can edit template messages anytime in <NextAnchor color="text-xweak" href="/settings/receivables" target="_blank">settings &gt; receivables</NextAnchor></Text>)}
                        plain/>
                </Box>
                <Box align="end" gap="small" direction="row-responsive" fill="horizontal" margin={{top: "medium", bottom: "small"}} justify="between">
                    {size !== "small" && (
                        <Button
                            secondary
                            event={{section: 'preview_send_invoice_email_modal', element: 'cancel'}}
                            label="Cancel"
                            onClick={onClose} />)}
                    <Box direction="row-responsive" align="center" gap="small">
                        {secondaryBtnText && secondaryBtnOnClick && (
                            <Button
                                event={{section: 'preview_send_invoice_email_modal', element: snakeCase(secondaryBtnText)}}
                                secondary
                                label={secondaryBtnText}
                                onClick={secondaryBtnOnClick} />
                        )}
                        <Button
                            event={{section: 'preview_send_invoice_email_modal', element: snakeCase(submitBtnText)}}
                            type="submit"
                            primary
                            label={submitBtnText} />
                    </Box>
                </Box>
            </Form>
        </Modal>
    )
};

export default PreviewEmailModal;
